// @ts-strict-ignore
import {ParamComponent} from 'panel';
import isEqual from 'lodash/isEqual';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {
  CellClickEvent,
  CellCoordinates,
  CollapseChangeEvent,
  SortChangeEvent,
} from 'common/components/data-table/types';
import {hasRetentionData} from 'common/report/retention/util/parsing';
import type {RowWithAggregate} from 'common/report/retention/util/parsing';
import type {MultimetricData} from 'common/report/insights/charts/retention-curve-chart/types';
import {SortConfig} from 'common/types/reports/bookmark';
import {RetentionDataTableRow} from 'common/report/retention/util/types';
import {RetentionPopover} from 'common/report/retention/visualization/popover';
import {RetentionBookmarkController} from 'common/report/retention/bookmark-controller';

import {template} from './template';
import css from './index.inline.styl';

interface TablePopoverState {
  open: boolean;
  popover?: RetentionPopover;
  retainedPercent?: string;
  dropOffPercent?: string;
  bgColor?: string;
  textColor?: string;
  rowIdx?: number;
  groupedRowIdx?: number;
  columnIdx?: number;
  absoluteColumnIdx?: number;
  targetCell?: HTMLElement;
}

interface InsightsRetentionTableState {
  bookmarkController: RetentionBookmarkController;
  results: RowWithAggregate[];
  popoverState: TablePopoverState;
}

type InsightsRetentionTableParams = {
  rowData: RowWithAggregate[];
  bookmarkData: MultimetricData;
  projectId: number;
  projectTimezone: string;
  resizingLayout: boolean;
  onSortChange: (sorting: SortConfig) => void;
  onExpandedSegmentsChange: (expandedSegments: string[]) => void;
};

type InsightsRetentionTableJSX = {
  params?: InsightsRetentionTableParams;
};

export class InsightsRetentionTable extends ParamComponent<
  InsightsRetentionTableParams,
  InsightsRetentionTableState,
  ChartsContextRegistry
> {
  get config() {
    return {
      css,
      useShadowDom: true,
      template,
      contexts: [`reportLayoutManager` as const, `i18n` as const, `metrics` as const],
      defaultState: {
        bookmarkController: null,
        results: null,
        popoverState: {
          open: false,
        },
      },
      params: {
        bookmarkData: Object,
        onSortChange: Function,
        onExpandedSegmentsChange: Function,
        projectId: Number,
        projectTimezone: String,
        resizingLayout: Boolean,
        rowData: Array,
      },
      hooks: {
        preUpdate: (_newState, newParams?: InsightsRetentionTableParams) => {
          if (newParams && !isEqual(newParams.bookmarkData, this.params.bookmarkData)) {
            this.handleBookmarkDataUpdate(newParams);
          }
          if (newParams && !isEqual(newParams.rowData, this.params.rowData)) {
            this.update({results: newParams.rowData});
          }
        },
      },
    };
  }

  connectedCallback() {
    this.update({
      results: this.params.rowData,
    });
    this.handleBookmarkDataUpdate(this.params);
    super.connectedCallback();
  }

  handleBookmarkDataUpdate(params: InsightsRetentionTableParams) {
    const {bookmark, queryResults} = params.bookmarkData;
    if (this.state.bookmarkController && this.state.bookmarkController.isEquivalent(bookmark, queryResults)) {
      return;
    }
    this.update({
      bookmarkController: new RetentionBookmarkController(bookmark, queryResults),
    });
  }

  hasResults() {
    return hasRetentionData(this.state.results);
  }

  handleSortChange = (ev: SortChangeEvent) => {
    const sorting = ev.detail.sorting;
    this.params?.onSortChange(sorting);
  };

  handleCollapseChange = (ev: CollapseChangeEvent) => {
    if (!this.state.bookmarkController) {
      return;
    }
    const {row, isCollapsed} = ev.detail;
    const expandedSegmentSet = new Set(this.state.bookmarkController?.getLegendExpandedSegments());
    if (isCollapsed) {
      expandedSegmentSet.delete(row.identifier);
    } else {
      expandedSegmentSet.add(row.identifier);
    }
    this.params?.onExpandedSegmentsChange(Array.from(expandedSegmentSet));
  };

  handlePopoverChange = (open: boolean) => {
    this.update({popoverState: {open}});
  };

  getSelectedCell(): null | CellCoordinates {
    const {popoverState} = this.state;
    if (popoverState.open) {
      return {
        columnIdx: popoverState.absoluteColumnIdx,
        rowIdx: popoverState.rowIdx,
        groupedRowIdx: popoverState.groupedRowIdx,
      };
    }
    return null;
  }

  handleCellClick = (ev: CellClickEvent) => {
    if (!this.state.bookmarkController) {
      return;
    }
    const {field, context, target, bgColor, textColor} = ev.detail;
    const row = ev.detail.row as RetentionDataTableRow;
    const columnIdx = parseInt(field);
    if (isNaN(columnIdx) || columnIdx < 0 || columnIdx >= row.cells.length) {
      return;
    }

    const cell = row.cells[columnIdx];
    const enteredValue =
      this.state.bookmarkController.hasMultipleRetentionMetrics() && this.state.bookmarkController.isSegmented()
        ? cell.entered_value.split(` / `).slice(1).join(` / `)
        : cell.entered_value;

    const formatter = this.state.bookmarkController.popoverFormatter(
      this.getContext(`metrics`),
      row.matchingShowClauseIndex,
    );

    const popover = this.state.bookmarkController.createPopover({
      matchingShowClauseIndex: row.matchingShowClauseIndex,
      projectTimezone: this.params.projectTimezone,
      colIdx: columnIdx,
      rowIdx: context.rowIdx,
      groupedRowIdx: context.groupedRowIdx,
      absoluteValue: formatter(cell.value, false),
      absoluteDropOffValue: formatter(cell.absolute_drop_off_value, false),
      cohortSize: cell.cohort_size,
      completeDate: cell.complete_date,
      enteredValue,
      enteredDate: cell.date,
      incomplete: cell.incomplete,
      isAggregated: !row.isGroupedRow,
      i18n: this.getContext(`i18n`),
    });
    this.update({
      popoverState: {
        retainedPercent: formatter(cell.percent, true),
        dropOffPercent: formatter(cell.percent_drop_off_value, true),
        bgColor,
        textColor,
        columnIdx,
        absoluteColumnIdx: context.columnIdx,
        rowIdx: context.rowIdx,
        groupedRowIdx: context.groupedRowIdx,
        popover,
        open: true,
        targetCell: target,
      },
    });
  };
}

export const InsightsRetentionTableEl = defineAndMakeJsxFactory<InsightsRetentionTableJSX>(
  `insights-retention-table`,
  InsightsRetentionTable,
);
export const InsightsRetentionTableReact = panel2react<InsightsRetentionTableJSX>(
  `insights-retention-table`,
  InsightsRetentionTable,
);
